import type { PayloadSnapshot, Row } from './types'
import type { ChangelogEntry, DueTimelineEntry } from './history'
import { dueOn, openOn } from './history'

export interface MonthStat {
  vol: number
  over: number
  paid: number
  unknown: number
  reg: number
  dueUnknown: number
}

export function monthEnds(asOf: string, since: string | null = null, count = 12): string[] {
  const results: string[] = []
  let year = Number(asOf.slice(0, 4))
  let month = Number(asOf.slice(5, 7))
  for (let index = 0; index < count; index++) {
    const isCurrent = year === Number(asOf.slice(0, 4)) && month === Number(asOf.slice(5, 7))
    const end = isCurrent
      ? asOf
      : new Date(Date.UTC(year, month, 0)).toISOString().slice(0, 10)
    results.push(end)
    if (since && `${end.slice(0, 7)}-01` <= since) break
    month--
    if (month === 0) {
      year--
      month = 12
    }
  }
  return results.reverse()
}

export function monthlyState(
  entries: ChangelogEntry[],
  ends: string[],
  entered: string | null,
  dueTimeline: DueTimelineEntry[],
  currentDue: string | null,
  currentStatus: string,
  resolved: string | null,
  categoryOf: Record<string, string | undefined>,
  bits: PayloadSnapshot['mbits'],
  dueBadFrom: string | null = null,
): number[] {
  return ends.map((end) => {
    let state = 0
    if (!entered || entered > end) return state
    state |= bits.reg
    const open = openOn(entries, currentStatus, end, categoryOf)
    if (open === null) state |= bits.unknown
    else if (open) {
      state |= bits.open
      if (dueBadFrom && dueBadFrom <= end) state |= bits.due_unknown
      else {
        const due = dueOn(dueTimeline, currentDue, end)
        if (due && due < end) state |= bits.overdue
      }
    }
    if (resolved && `${end.slice(0, 7)}-01` <= resolved && resolved <= end) state |= bits.resolved
    return state
  })
}

export function monthIndex(months: string[], selected: string): number {
  const candidate = selected === '' ? months.length - 1 : Number(selected)
  return Math.max(0, Math.min(months.length - 1, Number.isNaN(candidate) ? months.length - 1 : candidate))
}

export function monthStat(rows: Row[], index: number, bits: PayloadSnapshot['mbits']): MonthStat {
  return rows.reduce<MonthStat>((stat, row) => {
    const state = row.months[index] || 0
    if (!(state & bits.reg)) return stat
    stat.reg++
    if (state & bits.unknown) stat.unknown++
    if (state & bits.open) stat.vol++
    if (state & bits.overdue) stat.over++
    if (state & bits.due_unknown) stat.dueUnknown++
    if (state & bits.resolved) stat.paid++
    return stat
  }, { vol: 0, over: 0, paid: 0, unknown: 0, reg: 0, dueUnknown: 0 })
}
